import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {Box, Center, Show, Space, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {Trans} from 'react-i18next';

import {isNotNil} from 'ramda';

import {
  EmailCredentialsOwnerType,
  OAuthProviders,
  usePatchOAuthEmailCredentialsMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {getSmtpProviderDocumentationLink} from '../utils/getSmtpProviderDocumentationLink';
import {getSmtpProviderLogo} from '../utils/getSmtpProviderLogo';

interface EmailOAuthServiceForm {
  credentialsId: string;
  defaultValues: Partial<FormValues>;
  ownerType: EmailCredentialsOwnerType;
  providerCode: OAuthProviders;
}

export function EmailOAuthServiceForm(props: EmailOAuthServiceForm) {
  const providerCode = props.providerCode ?? 'custom-smtp';

  const [patchOAuthCredentials] = usePatchOAuthEmailCredentialsMutation();

  const handleSubmit: FormSubmitHandler<FormValues> = async (values) => {
    await patchOAuthCredentials({
      id: props.credentialsId,
      patchOAuthEmailCredentialsRequestBody: values,
    })
      .unwrap()
      .then(() => {
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  const providerLogo = getSmtpProviderLogo(providerCode);
  const providerDocumentationLink = getSmtpProviderDocumentationLink(providerCode);

  return (
    <Form<FormValues>
      defaultValues={props.defaultValues}
      onSubmit={handleSubmit}
      experimentalZodSchema={EditEmailServiceFormSchema}
    >
      {(control) => (
        <VStack spacing={4}>
          <Show when={isNotNil(providerLogo)}>
            <Center width="100%" height="100%">
              <Space vertical={4} />
              <Box width={40} height={40}>
                <Center width="100%" height="100%">
                  {providerLogo}
                </Center>
              </Box>
            </Center>
          </Show>

          <VStack>
            <Text align="center" size="small" color="secondary">
              <Trans
                i18nKey={`entity.emailService.labels.${providerCode}-documentationMessage`}
                components={{
                  break: <br />,
                  bold: <Text alternative inline size="small" color="secondary" />,
                }}
              />
            </Text>

            <Button
              variant="link"
              title={i18n.t('general.actions.viewDocumentation')}
              onClick={() =>
                window.open(providerDocumentationLink, '_blank', 'noopener,noreferrer')
              }
              leftIcon="action/open_in_new"
            />
          </VStack>

          <VStack spacing={4}>
            <FormField
              name="title"
              type="text"
              label={i18n.t('entity.emailService.labels.title')}
              control={control}
              tooltip={i18n.t(`entity.emailService.labels.${providerCode}-titleTooltip`)}
              isRequired
            />

            <FormField
              name="emailAddress"
              type="email"
              label={i18n.t('entity.emailService.labels.emailAddress')}
              tooltip={i18n.t(`entity.emailService.labels.${providerCode}-emailAddressTooltip`)}
              control={control}
              isRequired
            />

            <Separator spacing={0} />

            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discard')}
                variant="secondary"
                onClick={closeCurrentDialog}
              />
              <FormButton
                control={control}
                type="submit"
                title={i18n.t('general.actions.saveChanges')}
              />
            </ButtonGroup>
          </VStack>
        </VStack>
      )}
    </Form>
  );
}

const EditEmailServiceFormSchema = z.object({
  title: z.string().min(1),
  emailAddress: z.string().email(),
});

type FormValues = z.infer<typeof EditEmailServiceFormSchema>;
